// 3D LUT (.cube) の解析・タイル化・サンプリング (純ロジック)。
// タイル化: N 枚のスライス (青の index) を横に並べた N*N × N の RGBA8 画像。
// テクセル (z*N + x, y) = LUT[r=x, g=y, b=z]。GPU も CPU も同じ配置を読む。

export interface Cube3D {
  size: number
  /** r + g*N + b*N*N 順、各 3 要素 (0..1) */
  data: Float32Array
  title?: string
}

export function parseCube(text: string): Cube3D {
  let size = 0
  let size1d = 0
  let title: string | undefined
  let dmin = [0, 0, 0], dmax = [1, 1, 1]
  const values: number[] = []
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line || line.startsWith('#')) continue
    const up = line.toUpperCase()
    if (up.startsWith('TITLE')) { title = line.slice(5).trim().replace(/^"|"$/g, ''); continue }
    if (up.startsWith('LUT_3D_SIZE')) { size = parseInt(line.split(/\s+/)[1] ?? '0', 10); continue }
    if (up.startsWith('LUT_1D_SIZE')) { size1d = parseInt(line.split(/\s+/)[1] ?? '0', 10); continue }
    if (up.startsWith('DOMAIN_MIN')) { dmin = line.split(/\s+/).slice(1, 4).map(Number); continue }
    if (up.startsWith('DOMAIN_MAX')) { dmax = line.split(/\s+/).slice(1, 4).map(Number); continue }
    if (/^[-+0-9.eE\s]+$/.test(line)) {
      const nums = line.split(/\s+/).map(Number)
      if (nums.length >= 3 && nums.slice(0, 3).every(Number.isFinite)) values.push(nums[0]!, nums[1]!, nums[2]!)
    }
  }
  const norm = (v: number, c: number) => { const lo = dmin[c] ?? 0, hi = dmax[c] ?? 1; return hi > lo ? (v - lo) / (hi - lo) : v }
  if (size >= 2) {
    if (values.length < size * size * size * 3) throw new Error(`.cube のデータが足りません (${values.length / 3} / ${size * size * size})`)
    const data = new Float32Array(size * size * size * 3)
    for (let i = 0; i < size * size * size; i++) for (let c = 0; c < 3; c++) data[i * 3 + c] = Math.max(0, Math.min(1, norm(values[i * 3 + c]!, c)))
    return { size, data, title }
  }
  if (size1d >= 2) {
    // 1D LUT: 各チャンネル独立 → 17 段の 3D に展開
    if (values.length < size1d * 3) throw new Error('.cube のデータが足りません')
    const N = 17
    const data = new Float32Array(N * N * N * 3)
    const lut1 = (c: number, t: number) => {
      const p = t * (size1d - 1), i0 = Math.floor(p), i1 = Math.min(size1d - 1, i0 + 1), f = p - i0
      return norm(values[i0 * 3 + c]! * (1 - f) + values[i1 * 3 + c]! * f, c)
    }
    for (let b = 0; b < N; b++) for (let g = 0; g < N; g++) for (let r = 0; r < N; r++) {
      const i = (r + g * N + b * N * N) * 3
      data[i] = lut1(0, r / (N - 1)); data[i + 1] = lut1(1, g / (N - 1)); data[i + 2] = lut1(2, b / (N - 1))
    }
    return { size: N, data, title }
  }
  throw new Error('LUT_3D_SIZE (または LUT_1D_SIZE) がありません')
}

/** タイル化した RGBA8 画像 (幅 N*N、高さ N) */
export function cubeToTiledRgba(cube: Cube3D): { w: number; h: number; rgba: Uint8ClampedArray } {
  const N = cube.size
  const w = N * N, h = N
  const rgba = new Uint8ClampedArray(w * h * 4)
  for (let b = 0; b < N; b++) for (let g = 0; g < N; g++) for (let r = 0; r < N; r++) {
    const i = (r + g * N + b * N * N) * 3
    const o = (g * w + b * N + r) * 4
    rgba[o] = Math.round(cube.data[i]! * 255); rgba[o + 1] = Math.round(cube.data[i + 1]! * 255); rgba[o + 2] = Math.round(cube.data[i + 2]! * 255); rgba[o + 3] = 255
  }
  return { w, h, rgba }
}

/** タイル画像を三線形補間で読む (GPU と同じ: スライス内はバイリニア、スライス間は線形)。入力 / 出力 0..1 */
export function sampleTiledLut(rgba: Uint8ClampedArray, N: number, r: number, g: number, b: number): [number, number, number] {
  const w = N * N
  const x = Math.max(0, Math.min(1, r)) * (N - 1), y = Math.max(0, Math.min(1, g)) * (N - 1), z = Math.max(0, Math.min(1, b)) * (N - 1)
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z)
  const x1 = Math.min(N - 1, x0 + 1), y1 = Math.min(N - 1, y0 + 1), z1 = Math.min(N - 1, z0 + 1)
  const fx = x - x0, fy = y - y0, fz = z - z0
  const at = (xi: number, yi: number, zi: number, c: number) => rgba[(yi * w + zi * N + xi) * 4 + c]! / 255
  const out: [number, number, number] = [0, 0, 0]
  for (let c = 0; c < 3; c++) {
    const s0 = (at(x0, y0, z0, c) * (1 - fx) + at(x1, y0, z0, c) * fx) * (1 - fy) + (at(x0, y1, z0, c) * (1 - fx) + at(x1, y1, z0, c) * fx) * fy
    const s1 = (at(x0, y0, z1, c) * (1 - fx) + at(x1, y0, z1, c) * fx) * (1 - fy) + (at(x0, y1, z1, c) * (1 - fx) + at(x1, y1, z1, c) * fx) * fy
    out[c] = s0 * (1 - fz) + s1 * fz
  }
  return out
}

export const LUT_PRESETS = ['identity', 'sepia', 'warm', 'cool', 'crisp', 'faded', 'mono'] as const
export type LutPreset = (typeof LUT_PRESETS)[number]

/** 手続き的な組み込み LUT */
export function builtinCube(name: LutPreset, N = 17): Cube3D {
  const data = new Float32Array(N * N * N * 3)
  const clamp = (v: number) => Math.max(0, Math.min(1, v))
  for (let bi = 0; bi < N; bi++) for (let gi = 0; gi < N; gi++) for (let ri = 0; ri < N; ri++) {
    const r = ri / (N - 1), g = gi / (N - 1), b = bi / (N - 1)
    let o: [number, number, number] = [r, g, b]
    const lum = 0.299 * r + 0.587 * g + 0.114 * b
    switch (name) {
      case 'identity': break
      case 'sepia': o = [clamp(0.393 * r + 0.769 * g + 0.189 * b), clamp(0.349 * r + 0.686 * g + 0.168 * b), clamp(0.272 * r + 0.534 * g + 0.131 * b)]; break
      case 'warm': o = [clamp(r * 1.08 + 0.02), clamp(g * 1.02), clamp(b * 0.9)]; break
      case 'cool': o = [clamp(r * 0.92), clamp(g * 1.0), clamp(b * 1.08 + 0.03)]; break
      case 'crisp': { const k = (v: number) => clamp(0.5 + (v - 0.5) * 1.25); o = [k(r), k(g), k(b)]; break }
      case 'faded': { const k = (v: number) => clamp(0.08 + v * 0.84); o = [k(r), k(g), k(b) + 0.02]; break }
      case 'mono': o = [lum, lum, lum]; break
    }
    const i = (ri + gi * N + bi * N * N) * 3
    data[i] = o[0]; data[i + 1] = o[1]; data[i + 2] = o[2]
  }
  return { size: N, data, title: name }
}
